/**
 * AllPositionsLedger — ONE table that shows EVERY open option position in one place:
 * manual/Architect baskets, staged-campaign tranches, and auto-engine cycles, with a
 * consistent P&L / ROI / risk column (audit C4). It's the index; the rich management
 * cards (adjust / close / decision log) live in the detail sections below and on the
 * Option-AT page, reachable from each row.
 *
 * Read-only aggregation of two existing endpoints (baskets + engine instances) — no new
 * money math (uses the shared positionRows normaliser). Polls every 15s.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Table, TableHead, TableRow, TableCell, TableBody, ToggleButton,
  ToggleButtonGroup, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { optionsService } from '../../services/options.service';
import { optionStrategyService } from '../../services/optionStrategy.service';
import { useOptionColors, inr0, inrSigned } from './shared/OptionsUi';
import { basketRows, engineRows, orderRows, rowTotals, PositionRow, PositionOrigin } from './positionRows';

const ORIGIN_META: Record<PositionOrigin, { label: string; color: string }> = {
  basket: { label: 'Basket', color: '#1e88e5' },
  campaign: { label: 'Campaign', color: '#8e24aa' },
  engine: { label: 'Auto', color: '#00897b' },
};
const FLAG_META: Record<NonNullable<PositionRow['flag']>, { label: string; color: string; tip: string }> = {
  desync: { label: '⚠ out of sync', color: '#c62828', tip: 'Broker positions no longer match — auto TP/SL is frozen. Resolve on the Options page.' },
  attention: { label: '⚠ review', color: '#b26a00', tip: 'Price has moved against the plan — this position may need adjustment.' },
  thin: { label: '⚠ thin edge', color: '#b26a00', tip: 'Low reward:risk at entry.' },
};

const expiryLabel = (ms: number | null): { text: string; soon: boolean } => {
  if (!ms) return { text: '—', soon: false };
  const d = new Date(ms);
  const soon = ms - Date.now() < 24 * 3600e3 && ms - Date.now() > 0;
  return { text: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), soon };
};

type Scope = 'open' | 'all';
type ModeF = 'both' | 'LIVE' | 'PAPER';

const AllPositionsLedger: React.FC = () => {
  const c = useOptionColors();
  const navigate = useNavigate();
  const [rows, setRows] = useState<PositionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState<Scope>('open');
  const [origin, setOrigin] = useState<'all' | PositionOrigin>('all');
  const [modeF, setModeF] = useState<ModeF>('both');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [br, er] = await Promise.all([
        optionsService.getBaskets(undefined as any).then((r: any) => r?.data?.data || r?.data || []).catch(() => []),
        optionStrategyService.list().then((r: any) => r.data?.data || []).catch(() => []),
      ]);
      setRows(orderRows([...basketRows(br), ...engineRows(er)]));
    } catch { /* keep last */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, [load]);

  const filtered = useMemo(() => rows.filter(r =>
    (scope === 'all' || r.isOpen) &&
    (origin === 'all' || r.origin === origin) &&
    (modeF === 'both' || r.mode === modeF),
  ), [rows, scope, origin, modeF]);
  const totals = useMemo(() => rowTotals(filtered), [filtered]);

  const manage = (r: PositionRow) => {
    if (r.manage === 'engines') { navigate('/option-strategies'); return; }
    document.getElementById('hub-positions-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cell = { fontSize: '0.72rem', px: 1, py: 0.5, whiteSpace: 'nowrap' as const };
  const counts = useMemo(() => {
    const openAll = rows.filter(r => r.isOpen);
    return {
      all: openAll.length,
      basket: openAll.filter(r => r.origin === 'basket').length,
      campaign: openAll.filter(r => r.origin === 'campaign').length,
      engine: openAll.filter(r => r.origin === 'engine').length,
    };
  }, [rows]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800 }}>All option positions</Typography>
        <Chip size="small" label={`${totals.openCount} open`} color={totals.openCount ? 'info' : 'default'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700 }} />
        {(totals.openCount > 0) && <Chip size="small" label={`P&L ${inrSigned(totals.livePnl)}`} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800, bgcolor: totals.livePnl >= 0 ? c.profitBg : c.lossBg, color: totals.livePnl >= 0 ? c.profit : c.loss }} />}
        {(totals.risk > 0) && <Chip size="small" label={`Risk ${inr0(totals.risk)}`} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: c.lossBg, color: c.loss }} />}
        {loading && <CircularProgress size={13} />}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_e, v) => v && setScope(v)} sx={{ '& .MuiToggleButton-root': { py: 0, px: 0.9, fontSize: '0.62rem' } }}>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={modeF} onChange={(_e, v) => v && setModeF(v)} sx={{ '& .MuiToggleButton-root': { py: 0, px: 0.9, fontSize: '0.62rem' } }}>
            <ToggleButton value="both">Both</ToggleButton>
            <ToggleButton value="LIVE">Real</ToggleButton>
            <ToggleButton value="PAPER">Paper</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        {([['all', `All (${counts.all})`], ['basket', `Baskets (${counts.basket})`], ['campaign', `Campaigns (${counts.campaign})`], ['engine', `Auto-engines (${counts.engine})`]] as [typeof origin, string][]).map(([k, lbl]) => (
          <Chip key={k} size="small" label={lbl} clickable variant={origin === k ? 'filled' : 'outlined'} color={origin === k ? 'primary' : 'default'}
            onClick={() => setOrigin(k)} sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700 }} />
        ))}
      </Box>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {loading ? 'Loading…' : scope === 'open' ? 'No open option positions. Build a trade or start an auto-engine.' : 'No positions match this filter.'}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Table size="small" sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow>{['Where', 'Symbol', 'Structure', 'Mode', 'P&L', 'ROI', 'Max P / Risk', 'Expiry', 'Status', ''].map((h, i) => (
                <TableCell key={h || `x${i}`} align={['P&L', 'ROI'].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: '0.62rem', px: 1, py: 0.5, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>{h}</TableCell>
              ))}</TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => {
                const om = ORIGIN_META[r.origin];
                const exp = expiryLabel(r.expiryMs);
                const pnlColor = r.pnl == null ? 'text.disabled' : r.pnl >= 0 ? 'success.main' : 'error.main';
                return (
                  <TableRow key={r.key} hover>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={om.label} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, bgcolor: om.color, color: '#fff' }} />
                        <Typography component="span" sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{r.originLabel}</Typography>
                        {r.flag && <Tooltip arrow title={FLAG_META[r.flag].tip}><Chip size="small" label={FLAG_META[r.flag].label} sx={{ height: 15, fontSize: '0.48rem', fontWeight: 800, bgcolor: FLAG_META[r.flag].color, color: '#fff' }} /></Tooltip>}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 700 }}>{r.symbol}</TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: 'normal' }}>{r.structure}</TableCell>
                    <TableCell sx={cell}><Chip size="small" label={r.mode} color={r.mode === 'PAPER' ? 'warning' : 'error'} sx={{ height: 15, fontSize: '0.5rem', fontWeight: 700 }} /></TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: 800, color: pnlColor }}>{r.pnl == null ? '—' : inrSigned(r.pnl)}</TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: 700, color: r.roi == null ? 'text.disabled' : r.roi >= 0 ? 'success.main' : 'error.main' }}>{r.roi == null ? '—' : `${r.roi >= 0 ? '+' : ''}${r.roi.toFixed(1)}%`}</TableCell>
                    <TableCell sx={cell}>
                      <span style={{ color: '#26a69a' }}>{r.maxProfit != null ? inr0(r.maxProfit) : '—'}</span>
                      {r.risk != null && <Typography component="span" sx={{ fontSize: '0.64rem', color: 'error.main' }}> / {inr0(r.risk)}</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...cell, color: exp.soon ? 'warning.main' : 'text.secondary', fontWeight: exp.soon ? 700 : 400 }}>{exp.text}</TableCell>
                    <TableCell sx={cell}><Chip size="small" label={r.status} color={r.isOpen ? 'info' : 'default'} sx={{ height: 15, fontSize: '0.5rem', fontWeight: 700 }} /></TableCell>
                    <TableCell sx={{ px: 0.5, py: 0 }}>
                      <Tooltip arrow title={r.manage === 'engines' ? 'Manage on the Auto-trade engines page' : 'Manage in the detail cards below'}>
                        <IconButton size="small" onClick={() => manage(r)}>
                          {r.manage === 'engines' ? <OpenInNewIcon sx={{ fontSize: 15 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default AllPositionsLedger;
